// React

import { useSearchParams } from 'next/navigation';
import { MessageDescriptor, useIntl } from 'react-intl';

import Image from '@/components/image/NextImage';
// Hooks
import { useAnalytics } from '@/hooks/analytics';
import { useShareToken } from '@/hooks/share/useShareToken';
import { useDeviceState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import redirect from '@/utils/legacy/redirect';

// Data, Utilities, and Translation Strings
import { mobileHandoff } from './data';
import { mobileHandoffMessages } from './messages';
// Styles
import {
	Content,
	Description,
	DesktopDeviceContainer,
	H1Wrapper,
	Logo,
	OnboardingImageContainer,
	OnboardingImageWrapper,
	OnboardingNavButtonHandoff,
	QRCodeInstructions,
	QRCodeWrapper,
	RecommendationSubTitle,
	RecommendationTitle,
	Title,
} from './styles';
// Types
import { MobileHandoffFocus } from './types';

const MobileHandoff = () => {
	const { formatMessage } = useIntl();
	const { logEvent, logEventAsync } = useAnalytics();
	const { activeFlow, skippedGoals } = { activeFlow: 'stress', skippedGoals: false };

	const { isMobile } = useDeviceState();
	const searchParams = useSearchParams();
	const shareToken = useShareToken();
	const isSecondFreeTrial = searchParams?.get('source') === 'second_free_trial_offer';

	function getScreenTitle(): MessageDescriptor {
		return mobileHandoffMessages.heading;
	}

	function getProgram(): MobileHandoffFocus {
		return skippedGoals || isSecondFreeTrial ? mobileHandoff.skipped : mobileHandoff[activeFlow];
	}

	const { CTA, description, image, subtitle, title, url, qrCode = '' } = getProgram();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Page : Viewed',
			eventProps: {
				page_name: 'premium-confirmation',
			},
		});

		if (!isMobile) {
			logEvent({
				eventName: 'QR Code : Viewed',
				eventProps: {
					source:
						activeFlow === 'secondFreeTrial'
							? 'second_free_trial_offer'
							: 'Web Handoff Stress/Sleep Onboarding',
				},
			});
		}
	});

	async function onSkip(): Promise<void> {
		await logEventAsync({
			eventName: 'Modal : Exited',
			eventProps: {
				type: isSecondFreeTrial ? 'second_free_trial_offer' : 'Web Handoff Stress/Sleep Onboarding',
			},
		});

		redirect(`/app/${isSecondFreeTrial ? 'stress' : activeFlow}`);
	}

	async function onContinue(): Promise<void> {
		await logEventAsync({
			eventName: 'Modal: Proceeded',
			eventProps: {
				type: isSecondFreeTrial ? 'second_free_trial_offer' : 'Web Handoff Stress/Sleep Onboarding',
			},
		});

		redirect(url);
	}
	const descriptionCopy =
		typeof shareToken === 'string'
			? formatMessage(mobileHandoffMessages.shareSubheading, {
					num: 5,
				})
			: formatMessage(description);

	return (
		<Content role="alert" aria-live="polite">
			<H1Wrapper tabIndex={-1}>
				<Logo src="/_n/images/calm-logo-square.png" alt="Calm" />
			</H1Wrapper>
			<Title>
				{formatMessage(getScreenTitle(), {
					space: <>&nbsp;</>,
				})}
			</Title>
			<Description>{descriptionCopy}</Description>
			<DesktopDeviceContainer skippedFTUE={skippedGoals}>
				<div>
					{Array.isArray(image) ? (
						<OnboardingImageContainer>
							{image.map(im => (
								<OnboardingImageWrapper key={`${activeFlow}_${im}`}>
									{/* TODO: Migrate to NextImage */}
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img src={im} alt={`onboarding ${activeFlow} image`} />
								</OnboardingImageWrapper>
							))}
						</OnboardingImageContainer>
					) : (
						<OnboardingImageWrapper>
							{/* TODO: Migrate to NextImage */}
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src={image} alt={`onboarding ${activeFlow} image`} />
						</OnboardingImageWrapper>
					)}

					{title && <RecommendationTitle>{formatMessage(title)}</RecommendationTitle>}

					{subtitle && <RecommendationSubTitle>{formatMessage(subtitle)}</RecommendationSubTitle>}
				</div>
				{}
				<QRCodeWrapper>
					<Image src={qrCode} alt="Calm" width={148} height={148} />
				</QRCodeWrapper>
				<QRCodeInstructions>
					{formatMessage(mobileHandoffMessages.qrCodeInstructions, {
						br: <br />,
					})}
				</QRCodeInstructions>
				<OnboardingNavButtonHandoff
					continueCta={CTA}
					isTransparentSkip
					onContinue={onContinue}
					onCustomSkip={onSkip}
					skipCta={mobileHandoffMessages.secondaryCta}
					skippedFTUE={skippedGoals}
				/>
			</DesktopDeviceContainer>
		</Content>
	);
};

export default MobileHandoff;
